/**
 * TanStack React Query Configuration
 * 
 * This module provides the QueryClient instance with conservative defaults
 * optimized for the MissionAssist CRM application.
 * 
 * Key Configuration:
 * - staleTime: 30 seconds for list queries (reasonable freshness without over-fetching)
 * - gcTime: 5 minutes (garbage collection time, formerly cacheTime)
 * - refetchOnWindowFocus: false (disabled to prevent aggressive refetching)
 * - refetchOnReconnect: true (refetch when connection is restored)
 * - retry: 1 (retry failed requests once)
 * 
 * P1 HARDENING:
 * - Runtime tenant assertion prevents null/undefined tenantId in query keys
 * - User preferences are now tenant-scoped to prevent cross-tenant cache bleed
 */

import { QueryClient } from "@tanstack/react-query";

// Determine environment for assertion behavior
const isDev = process.env.NODE_ENV === "development";
const isStaging = process.env.NODE_ENV !== "production";

/**
 * P1 HARDENING (A1): Runtime Tenant Assertion
 * 
 * Prevents creation of query keys with null/undefined tenantId.
 * - Dev/Staging: Throws error with stack trace
 * - Production: Console error + returns null to disable query
 * 
 * @param {string} tenantId - The tenant ID to validate
 * @param {string} context - Description of where the assertion is called from
 * @returns {string} The validated tenantId
 * @throws {Error} In dev/staging if tenantId is missing
 */
function assertTenantId(tenantId, context) {
  if (!tenantId) {
    const errorMessage = `[QUERY KEY ERROR] Missing tenantId in ${context}. This would cause cross-tenant cache issues.`;
    
    if (isDev || isStaging) {
      // In dev/staging, throw to surface the bug immediately
      throw new Error(errorMessage);
    } else {
      // In production, log error but don't crash
      console.error(errorMessage, new Error().stack);
      return null; // Will cause query to be disabled
    }
  }
  return tenantId;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 30 seconds
      staleTime: 30 * 1000,
      // Cache data for 5 minutes before garbage collection
      gcTime: 5 * 60 * 1000,
      // Disable aggressive refetch on window focus (we'll handle this manually where needed)
      refetchOnWindowFocus: false,
      // Refetch when network is restored
      refetchOnReconnect: true,
      // Retry failed requests once
      retry: 1,
      // Don't refetch on mount if data is still fresh
      refetchOnMount: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

/**
 * Query Key Factory
 * 
 * Centralized query key generation to ensure:
 * 1. Tenant-scoped caching (prevents cross-tenant cache bleed)
 * 2. Consistent key structure across the application
 * 3. Proper cache invalidation on mutations
 * 
 * P1 HARDENING:
 * - All factory functions validate tenantId at runtime
 * - Missing tenantId throws in dev/staging, logs + returns null in production
 */
export const queryKeys = {
  // Appointments
  appointments: {
    all: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.appointments.all");
      return ["appointments", tenantId];
    },
    list: (tenantId, filters, page, sort) => {
      assertTenantId(tenantId, "queryKeys.appointments.list");
      return ["appointments", tenantId, "list", { filters, page, sort }];
    },
    detail: (tenantId, appointmentId) => {
      assertTenantId(tenantId, "queryKeys.appointments.detail");
      return ["appointments", tenantId, "detail", appointmentId];
    },
    quotes: (tenantId, appointmentId) => {
      assertTenantId(tenantId, "queryKeys.appointments.quotes");
      return ["appointments", tenantId, "quotes", appointmentId];
    },
    discovery: (tenantId, appointmentId) => {
      assertTenantId(tenantId, "queryKeys.appointments.discovery");
      return ["appointments", tenantId, "discovery", appointmentId];
    },
    filters: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.appointments.filters");
      return ["appointments", tenantId, "filters"];
    },
    pendingSummaries: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.appointments.pendingSummaries");
      return ["appointments", tenantId, "pending-summaries"];
    },
  },
  
  // Leads
  leads: {
    all: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.leads.all");
      return ["leads", tenantId];
    },
    list: (tenantId, filters, page, sort) => {
      assertTenantId(tenantId, "queryKeys.leads.list");
      return ["leads", tenantId, "list", { filters, page, sort }];
    },
    detail: (tenantId, leadId) => {
      assertTenantId(tenantId, "queryKeys.leads.detail");
      return ["leads", tenantId, "detail", leadId];
    },
    filters: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.leads.filters");
      return ["leads", tenantId, "filters"];
    },
    household: (tenantId, leadId) => {
      assertTenantId(tenantId, "queryKeys.leads.household");
      return ["leads", tenantId, "household", leadId];
    },
  },
  
  // Clients
  clients: {
    all: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.clients.all");
      return ["clients", tenantId];
    },
    list: (tenantId, filters) => {
      assertTenantId(tenantId, "queryKeys.clients.list");
      return ["clients", tenantId, "list", { filters }];
    },
    detail: (tenantId, clientId) => {
      assertTenantId(tenantId, "queryKeys.clients.detail");
      return ["clients", tenantId, "detail", clientId];
    },
  },
  
  // P1 HARDENING (A2): User Preferences - Now tenant-scoped
  // This prevents cross-tenant preference bleed in React Query cache
  userPrefs: {
    all: (tenantId) => {
      assertTenantId(tenantId, "queryKeys.userPrefs.all");
      return ["userPrefs", tenantId];
    },
  },
};

export default queryClient;
